import { useQuery } from '@tanstack/react-query';
import { useSession } from '@/lib/auth';
import {
  fetchPortalSession,
  PortalSessionOperationError,
} from '@/pages/PartnerPortal/session/portalSession';

function portalAccessQueryKey(userId: string | null) {
  return ['portalAccess', userId] as const;
}

async function readPortalAccess(userId: string | null): Promise<boolean> {
  const result = await fetchPortalSession('admin');
  if (result.ok && result.snapshot.actor?.userId !== userId) {
    throw new PortalSessionOperationError(
      'Portal access response does not match the current account.'
    );
  }
  return result.ok;
}

/**
 * Resolve whether the current Atrivial account can open the Portal.
 * A failed lookup resolves as no portal access so marketing CTAs degrade
 * to the trial action instead of disappearing until a refetch succeeds.
 */
export function usePortalAccess() {
  const { user, authResolved, isAuthenticated } = useSession();
  const userId = user?.id ?? null;
  const portalAccessQuery = useQuery({
    queryKey: portalAccessQueryKey(userId),
    queryFn: () => readPortalAccess(userId),
    enabled: authResolved && isAuthenticated && userId !== null,
    staleTime: 0,
    retry: false,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  return {
    isAuthenticated,
    hasPortalAccess: isAuthenticated && portalAccessQuery.data === true,
    isPortalAccessResolved:
      authResolved &&
      (!isAuthenticated || portalAccessQuery.isSuccess || portalAccessQuery.isError),
  };
}
